import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { BeatLoader } from 'react-spinners';
import { QRCodeSVG } from 'qrcode.react';
import posthog from 'posthog-js';
import { toast } from 'react-hot-toast';
import { Amplify } from 'aws-amplify';
import { signIn, getCurrentUser, signOut, confirmSignIn, setUpTOTP, verifyTOTPSetup, updateMFAPreference } from 'aws-amplify/auth';
import awsconfig from '../../../src/aws-exports';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, Shield, QrCode, ArrowRight, X, Info } from 'lucide-react';

// Import the modern components we created
import SignUp from './SignUp';
import ForgotPassword from './ForgotPassword';
import { isUserRegistered } from '../../redux/actions/checkUserRegister';
import { updateUserActivity } from '../access/accessControlUtils';
import { fetchRBACWithCache } from '../../utils/rbacCache';
import Button from '../../ui-components/Button/Button';
Amplify.configure(awsconfig);

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [userData, setUserData] = useState({});
  const [showSignup, setShowSignup] = useState(false);
  const [showResetPass, setShowResetPass] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isMFA, setIsMFA] = useState(false);
  const [MFAconfirm, setMFAconfirm] = useState(false);
  const [loadingVerificationCode, setLoadingVerificationcode] = useState(false);
  const [loadingSignIn, setLoadingSignIn] = useState(false);
  const [isTOTPsetUpRequired, setIsTOTPsetUpRequired] = useState(false);
  const [TOTPsetupCode, setTOTPsetupCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [signInSession, setSignInSession] = useState(null);


  const checkUser = useSelector((state => state.checkUser));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleLoginKeyPress = (e) => {
    if (e.key === 'Enter' && email && password) {
      e.preventDefault();
      handleSignin();
    }
  };

  const handleMFAKeyPress = (e) => {
    if (e.key === 'Enter' && code) {
      e.preventDefault();
      if (isMFA) {
        confirmHandleLogin();
      } else if (!isTOTPsetUpRequired && TOTPsetupCode) {
        verifyTOTP();
      }
    }
  };

  useEffect(() => {
    const fetch_user_check = window.localStorage.getItem('fetch_user_check');
    if (submitted && MFAconfirm && fetch_user_check === 'true') {
      if (checkUser.response === 'true') {
        localStorage.setItem('email', email);
        updateUserActivity(email);
        navigate("/analytic");
      } else {
        let errorMsg = "You can not login now because this email is not yet registered with the Analytic services. Please contact Art of Logic support (support@artoflogictech.com) to register your email.";
        toast.error(errorMsg, { duration: 9000 });
        handleCancel();
      }
    }
  }, [navigate, checkUser, MFAconfirm, submitted]);

  const confirmHandleLogin = async () => {
    try {
      setLoadingSignIn(true);
      
      const responseConfirmSignIn = await confirmSignIn({
        challengeResponse: code
      });
      
      // console.log('MFA confirmation result:', responseConfirmSignIn);
      posthog.identify();
      
      setLoadingSignIn(false);
      setMFAconfirm(true);
      dispatch(isUserRegistered(email));
      await fetchAndStoreRBAC(email);
      
    } catch (error) {
      console.error('Error confirming sign in:', error);
      setLoadingSignIn(false);
      
      if (error.message === 'Invalid session for the user, session is expired.') {
        toast.error('Session is expired, please login again.');
        setIsMFA(false);
        // Reset form to allow fresh login
        setEmail('');
        setPassword('');
        setCode('');
      } else if (error.message === 'Invalid code received for user') {
        toast.error('Invalid code received. Please enter the correct code.');
      } else if (error.message.includes('SignInException')) {
        toast.error('Session expired. Please sign in again.');
        setIsMFA(false);
        setEmail('');
        setPassword('');
        setCode('');
      } else {
        toast.error('Error confirming sign in. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    setIsMFA(false);
    setEmail('');
    setPassword('');
    setCode('');
    setLoadingVerificationcode(false);
    setLoadingSignIn(false);
    setIsTOTPsetUpRequired(false);
    setTOTPsetupCode('');
    setMFAconfirm(false);
    setSubmitted(false);
    setSignInSession(null);
    localStorage.clear();
    navigate('/');
  };

  const verifyTOTP = async () => {
    try {
      setLoadingSignIn(true);
      
      // Use confirmSignIn with the challenge response
      const result = await confirmSignIn({
        challengeResponse: code
      });
      
      // console.log('TOTP verification result:', result);
      
      // Set TOTP as the preferred MFA method
      await updateMFAPreference({ totp: 'PREFERRED' });

      setLoadingSignIn(false);
      setMFAconfirm(true);
      
      // Clear the TOTP setup state to hide QR code
      setIsTOTPsetUpRequired(false);
      setTOTPsetupCode('');
      setCode(''); // Clear the code input
      
      dispatch(isUserRegistered(email));
      await fetchAndStoreRBAC(email);
      
    } catch (error) {
      console.error('Error verifying TOTP:', error);
      setLoadingSignIn(false);
      
      if (error.message === 'Invalid session for the user, session is expired.') {
        toast.error('Session is expired, please login again.');
        setIsMFA(false);
        setIsTOTPsetUpRequired(false);
        setTOTPsetupCode('');
      } else if (error.message.includes('Invalid code')) {
        toast.error('Invalid code. Please try again.');
      } else {
        toast.error('Error verifying code. Please try again.');
      }
    }
  };

  const handleTOTPSetup = (secretCode) => {
    try {
      setTOTPsetupCode(secretCode);
      setIsTOTPsetUpRequired(false);
    } catch (error) {
      console.error('Error setting up TOTP:', error);
      toast.error(error.message);
    }
  };

  const handleSignin = async () => {
    setSubmitted(true);
    setLoadingVerificationcode(true);

    try {
      let currentUser;
      try {
        currentUser = await getCurrentUser();
      } catch (err) {
        currentUser = null;
      }

      if (currentUser && currentUser.username !== email) {
        // console.log(`Signing out previous user: ${currentUser.username}`);
        await signOut();
      }

      const signInResult = await signIn({ username: email, password });
      // console.log('Sign in result:', signInResult);
      
      // Store the session for later use
      setSignInSession(signInResult);
      
      setLoadingVerificationcode(false);

      if (signInResult.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setIsMFA(true);
      }

      if (signInResult.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        setIsTOTPsetUpRequired(true);
        const secretcode = signInResult.nextStep.totpSetupDetails?.sharedSecret;
        handleTOTPSetup(secretcode);
      }

      if (signInResult.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        toast.error('You cannot login because sign up is pending. Please Sign up first.');
      }
    } catch (error) {
      setLoadingVerificationcode(false);
      console.error(error.message);
      if (error.message === 'There is already a signed in user.') {
        toast.error('You are already signed in. Please refresh or try logging out first.');
        setIsMFA(true);
      } else {
        toast.error(error.message);
      }
    }
  };

  // Use centralized RBAC caching service (force refresh on login)
  const fetchAndStoreRBAC = async (email) => {
    try {
      // Force refresh on login to get latest permissions
      await fetchRBACWithCache(email, true);
    } catch (error) {
      console.error('Error fetching RBAC data:', error);
      toast.error("Error fetching RBAC data.");
    }
  };

  const Modal = ({ show, onHide, children, title }) => {
    if (!show) return null;
    
    return (
      <div className="modal-overlay">
        <div className="modal-content-modern">
          <div className="modal-header-modern">
            <h3>{title}</h3>
            <button className="modal-close-btn" onClick={onHide}>
              <X size={20} />
            </button>
          </div>
          <div className="modal-body-modern">
            {children}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        * {
          box-sizing: border-box;
        }

        body {
          margin: 0;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          min-height: 100vh;
        }

        .login-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          position: relative;
        }

        .login-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="0.5" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>') repeat;
          opacity: 0.1;
          pointer-events: none;
        }

        .login-card {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 24px;
          padding: 40px;
          width: 100%;
          max-width: 420px;
          box-shadow: 
            0 20px 40px rgba(0, 0, 0, 0.1),
            0 0 0 1px rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.3);
          position: relative;
          z-index: 1;
          animation: slideUp 0.6s ease-out;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .login-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .login-title {
          font-size: 32px;
          font-weight: 700;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin: 0 0 8px 0;
        }

        .login-subtitle {
          color: #6b7280;
          font-size: 16px;
          margin: 0;
        }

        .form-group {
          margin-bottom: 24px;
          position: relative;
        }

        .input-container {
          position: relative;
        }

        .input-field {
          width: 100%;
          height: 52px;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          padding: 0 16px 0 48px;
          font-size: 16px;
          transition: all 0.3s ease;
          background: #fff;
          color: #1f2937;
        }

        .input-field:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          transform: translateY(-1px);
        }

        .input-field::placeholder {
          color: #9ca3af;
        }

        .input-icon {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
          transition: all 0.3s ease;
          pointer-events: none;
        }

        .input-field:focus + .input-icon {
          color: #667eea;
        }

        .password-toggle {
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          transition: all 0.3s ease;
          padding: 4px;
          border-radius: 6px;
        }

        .password-toggle:hover {
          color: #667eea;
          background: rgba(102, 126, 234, 0.1);
        }

        .login-btn {
          width: 100%;
          height: 52px;
          border: none;
          border-radius: 12px;
          color: white;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-bottom: 24px;
        }

        .login-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .mfa-container {
          background: #f8fafc;
          border: 2px solid #e2e8f0;
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 24px;
          text-align: center;
        }

        .mfa-icon {
          margin: 0 auto 16px auto;
          color: #667eea;
        }

        .mfa-title {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 8px 0;
        }

        .mfa-description {
          color: #6b7280;
          font-size: 14px;
          margin: 0 0 20px 0;
          line-height: 1.5;
        }

        .qr-container {
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 24px;
          text-align: center;
        }

        .qr-title {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 16px 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .qr-description {
          color: #6b7280;
          font-size: 14px;
          margin: 0 0 20px 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .qr-description-text {
          flex: 1;
        }

        .info-button {
          position: relative;
          background: none;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          padding: 2px;
          border-radius: 50%;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
        }

        .info-button:hover {
          color: #667eea;
          background: rgba(102, 126, 234, 0.1);
        }

        .info-tooltip {
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-bottom: 8px;
          background: #1f2937;
          color: white;
          padding: 8px 12px;
          border-radius: 8px;
          font-size: 12px;
          white-space: nowrap;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s ease;
          z-index: 1000;
          max-width: 200px;
          white-space: normal;
          text-align: center;
          line-height: 1.4;
        }

        .info-tooltip::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 5px solid transparent;
          border-top-color: #1f2937;
        }

        .info-button:hover .info-tooltip {
          opacity: 1;
          visibility: visible;
        }

        .qr-code {
          margin: 20px 0;
        }

        .footer-links {
          display: flex;
          flex-direction: column;
          gap: 12px;
          align-items: center;
          text-align: center;
        }

        .link-text {
          color: #6b7280;
          font-size: 14px;
        }

        .link-btn {
          background: none;
          border: none;
          color: #667eea;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          text-decoration: underline;
          transition: all 0.3s ease;
          padding: 4px 8px;
          border-radius: 6px;
        }

        .link-btn:hover {
          background: rgba(102, 126, 234, 0.1);
          text-decoration: none;
        }

        .cancel-btn {
          background: none;
          border: none;
          color: #9ca3af;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 16px;
          padding: 8px;
          border-radius: 6px;
        }

        .cancel-btn:hover {
          color: #ef4444;
          background: rgba(239, 68, 68, 0.1);
        }

        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 16px 0;
        }

        .disclaimer {
          text-align: center;
          color: #9ca3af;
          font-size: 12px;
          margin-top: 20px;
          line-height: 1.4;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-content-modern {
          background: white;
          border-radius: 20px;
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow: hidden;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
          animation: modalSlideUp 0.3s ease-out;
        }

        @keyframes modalSlideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .modal-header-modern {
          padding: 24px 24px 0 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 16px;
        }

        .modal-header-modern h3 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #1f2937;
        }

        .modal-close-btn {
          background: none;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          padding: 8px;
          border-radius: 8px;
          transition: all 0.3s ease;
        }

        .modal-close-btn:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .modal-body-modern {
          padding: 24px;
          overflow-y: auto;
        }

        @media (max-width: 768px) {
          .login-card {
            padding: 24px;
            margin: 16px;
            border-radius: 20px;
          }

          .login-title {
            font-size: 28px;
          }

          .input-field {
            height: 48px;
            padding: 0 12px 0 44px;
          }

          .input-icon {
            left: 12px;
          }

          .password-toggle {
            right: 12px;
          }

          .login-btn {
            height: 48px;
          }

          .modal-content-modern {
            margin: 16px;
            border-radius: 16px;
          }

          .qr-description {
            flex-direction: column;
            gap: 12px;
          }

          .info-tooltip {
            max-width: 180px;
            font-size: 11px;
          }
        }
      `}</style>

      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1 className="login-title">Welcome Back</h1>
            <p className="login-subtitle">Sign in to your account to continue</p>
          </div>

          <div onSubmit={(e) => { e.preventDefault(); handleSignin(); }}>
            <div className="form-group">
              <div className="input-container">
                <input
                  type="email"
                  className="input-field"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={handleLoginKeyPress}
                  required
                />
                <Mail className="input-icon" size={20} />
              </div>
            </div>

            <div className="form-group">
              <div className="input-container">
                <input
                  type={showPassword ? "text" : "password"}
                  className="input-field"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={handleLoginKeyPress}
                  required
                />
                <Lock className="input-icon" size={20} />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              variant="Primary"
              type="submit"
              className="login-btn"
              onClick={handleSignin}
              disabled={loadingVerificationCode}
            >
              {loadingVerificationCode ? (
                <BeatLoader size={6} color="#ffffff" />
              ) : (
                <>
                  Sign In
                  <ArrowRight size={20} />
                </>
              )}
            </Button>
          </div>

          {isTOTPsetUpRequired && (
            <button onClick={handleTOTPSetup} className="login-btn">
              Set up TOTP
            </button>
          )}

          {!isTOTPsetUpRequired && TOTPsetupCode && (
            <div className="qr-container">
              <div className="qr-description">
                <span className="qr-description-text">
                  Scan the QR code with your mobile authenticator app
                </span>
                <button className="info-button">
                  <Info size={16} />
                  <div className="info-tooltip">
                    Use your preferred mobile QR authentication system such as Google or Microsoft's
                  </div>
                </button>
              </div>

              {/* Add QR Code with error boundary */}
              <div className="qr-code">
                {TOTPsetupCode && (
                  <QRCodeSVG 
                    value={`otpauth://totp/ArtOfLogic:${email}?secret=${TOTPsetupCode}&issuer=ArtOfLogic`}
                    size={200}
                    level="M"
                    includeMargin={true}
                  />
                )}
              </div>

              <div className="form-group">
                <div className="input-container">
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Enter authenticator code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    onKeyPress={handleMFAKeyPress}
                  />
                  <Shield className="input-icon" size={20} />
                </div>
              </div>

              <Button
                variant="Primary"
                onClick={verifyTOTP}
                disabled={loadingSignIn}
              >
                {loadingSignIn ? (
                  <BeatLoader size={6} color="#ffffff" />
                ) : (
                  "Verify Code"
                )}
              </Button>
            </div>
          )}

          {isMFA && (
            <div className="mfa-container">
              <Shield size={32} className="mfa-icon" />
              <h3 className="mfa-title">Two-Factor Authentication</h3>
              <p className="mfa-description">
                Please enter the code from your registered authenticator app
              </p>

              <div className="form-group">
                <div className="input-container">
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Enter authenticator code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    onKeyPress={handleMFAKeyPress}
                  />
                  <Shield className="input-icon" size={20} />
                </div>
              </div>

              <Button
                variant="Primary"
                className="login-btn"
                onClick={confirmHandleLogin}
                disabled={loadingSignIn}
              >
                {loadingSignIn ? (
                  <BeatLoader size={6} color="#ffffff" />
                ) : (
                  "Verify Code"
                )}
              </Button>
            </div>
          )}

          <div className="footer-links">
            <div>
              <span className="link-text">Don't have an account? </span>
              <button className="link-btn" onClick={() => setShowSignup(true)}>
                Sign up
              </button>
            </div>

            <button className="link-btn" onClick={() => setShowResetPass(true)}>
              Forgot your password?
            </button>

            <button className="cancel-btn" onClick={handleCancel}>
              Cancel
            </button>
          </div>

          <div className="disclaimer">
            *If you lost your authenticator device, please contact Art of Logic support
          </div>
        </div>

        <Modal 
          show={showSignup} 
          onHide={() => setShowSignup(false)}
          title="Create a new account"
        >
          <SignUp onClose={() => setShowSignup(false)} />
        </Modal>

        <Modal 
          show={showResetPass} 
          onHide={() => setShowResetPass(false)}
          title="Reset your password"
        >
          <ForgotPassword username={email} onClose={() => setShowResetPass(false)} />
        </Modal>
      </div>
    </>
  );
};

export default LoginPage;